import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Avatar, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar, Tooltip,
  Divider, Tabs, Tab, Autocomplete, Paper
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Visibility as ViewIcon,
  Close as CloseIcon, Description as DescriptionIcon, LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon, MedicalServices as MedicalIcon,
  Refresh as RefreshIcon, CheckCircle as SignedIcon, Lock as LockIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const recordTypeLabels: Record<string, string> = {
  consultation_note: 'Nota de Consulta', anamnesis: 'Anamnese', evolution: 'Evolução',
  prescription: 'Prescrição', certificate: 'Atestado', exam_request: 'Pedido de Exame',
  referral: 'Encaminhamento',
};

type RecordType = 'consultation_note' | 'anamnesis' | 'evolution' | 'prescription' | 'certificate' | 'exam_request' | 'referral';

const MedicalRecords: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [records, setRecords] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [patientFilter, setPatientFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [tabValue, setTabValue] = useState(0);

  const initialFormData = {
    patient_id: '', professional_id: '', record_type: 'consultation_note' as RecordType, title: '', content: '',
    diagnosis: '', diagnosis_code: '', treatment_plan: '',
    anamnesis_data: { chief_complaint: '', history_present_illness: '', past_medical_history: '', family_history: '', social_history: '' },
    physical_exam: { blood_pressure: '', heart_rate: '', temperature: '', weight: '', height: '', general_appearance: '', observations: '' },
    prescriptions: [{ medication: '', dosage: '', frequency: '', duration: '', instructions: '' }],
    certificate_type: '', certificate_days: 0, certificate_cid: '',
    exam_requests: [{ exam_name: '', instructions: '', urgency: 'normal' as const }],
    referral_specialty: '', referral_reason: '',
    is_confidential: false,
  };

  const [formData, setFormData] = useState(initialFormData);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (patientFilter) params.patient_id = patientFilter;
      if (typeFilter) params.record_type = typeFilter;

      const response = await axios.get(getApiUrl('/clinic/medical-records'), { params });
      if (response.data.success) {
        setRecords(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching records:', err);
    } finally {
      setLoading(false);
    }
  }, [page, patientFilter, typeFilter]);

  const fetchAuxData = async () => {
    try {
      const [pRes, prRes] = await Promise.all([
        axios.get(getApiUrl('/clinic/patients'), { params: { limit: 500, status: 'active' } }),
        axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true } }),
      ]);
      setPatients(pRes.data.data || []);
      setProfessionals(prRes.data.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { fetchAuxData(); }, []);

  const handleSave = async () => {
    const missing: string[] = [];
    if (!formData.patient_id) missing.push('Paciente');
    if (!formData.professional_id) missing.push('Profissional');
    if (!formData.content?.trim()) missing.push('Conteúdo / Descrição');
    if (missing.length > 0) {
      setSnackbar({
        open: true,
        message: `Preencha os campos obrigatórios: ${missing.join(', ')}`,
        severity: 'error',
      });
      return;
    }
    try {
      setSaving(true);
      const payload: any = {
        patient_id: formData.patient_id,
        professional_id: formData.professional_id,
        record_type: formData.record_type,
        title: formData.title?.trim() || null,
        content: formData.content.trim(),
        diagnosis: formData.diagnosis?.trim() || null,
        diagnosis_code: formData.diagnosis_code?.trim() || null,
        treatment_plan: formData.treatment_plan?.trim() || null,
        is_confidential: formData.is_confidential,
      };

      if (formData.record_type === 'anamnesis') payload.anamnesis_data = formData.anamnesis_data;
      if (formData.physical_exam.blood_pressure) payload.physical_exam = formData.physical_exam;
      if (formData.record_type === 'prescription') payload.prescriptions = formData.prescriptions.filter(p => p.medication);
      if (formData.record_type === 'certificate') {
        payload.certificate_type = formData.certificate_type;
        payload.certificate_days = formData.certificate_days;
        payload.certificate_cid = formData.certificate_cid;
      }
      if (formData.record_type === 'exam_request') payload.exam_requests = formData.exam_requests.filter(e => e.exam_name);
      if (formData.record_type === 'referral') {
        payload.referral_specialty = formData.referral_specialty;
        payload.referral_reason = formData.referral_reason;
      }

      await axios.post(getApiUrl('/clinic/medical-records'), payload);
      setSnackbar({ open: true, message: 'Prontuário salvo com sucesso!', severity: 'success' });
      setFormData(initialFormData);
      setDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async (id: string, professionalId: string) => {
    try {
      await axios.patch(getApiUrl(`/clinic/medical-records/${id}/sign`), { professional_id: professionalId });
      setSnackbar({ open: true, message: 'Prontuário assinado!', severity: 'success' });
      fetchRecords();
      if (selectedRecord?.id === id) {
        const res = await axios.get(getApiUrl(`/clinic/medical-records/${id}`));
        setSelectedRecord(res.data.data);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao assinar', severity: 'error' });
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString('pt-BR');

  const addPrescription = () => {
    setFormData({ ...formData, prescriptions: [...formData.prescriptions, { medication: '', dosage: '', frequency: '', duration: '', instructions: '' }] });
  };

  const addExamRequest = () => {
    setFormData({ ...formData, exam_requests: [...formData.exam_requests, { exam_name: '', instructions: '', urgency: 'normal' }] });
  };

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e1b4b' }}>
          Prontuário Eletrônico
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
          setFormData(initialFormData);
          setDialogOpen(true);
          setTabValue(0);
        }}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          Novo Registro
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Autocomplete options={patients} getOptionLabel={(p: any) => p.full_name} sx={{ minWidth: 250 }}
          onChange={(_, v) => setPatientFilter(v?.id || '')}
          renderInput={(params) => <TextField {...params} label="Filtrar por paciente" size="small" />} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tipo de Registro</InputLabel>
          <Select value={typeFilter} label="Tipo de Registro" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(recordTypeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Atualizar"><IconButton onClick={fetchRecords}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Records List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <Grid container spacing={2}>
          {records.map((record) => (
            <Grid item xs={12} md={6} key={record.id}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', cursor: 'pointer', transition: 'all 0.2s',
                '&:hover': { borderColor: '#8b5cf6', boxShadow: '0 4px 16px rgba(139,92,246,0.1)' } }}
                onClick={() => { setSelectedRecord(record); setViewOpen(true); }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: '#f3e8ff', width: 36, height: 36 }}>
                        <DescriptionIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e1b4b' }}>
                          {record.title || recordTypeLabels[record.record_type] || 'Registro'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {record.clinic_patients?.full_name} • Dr(a). {record.clinic_professionals?.full_name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={recordTypeLabels[record.record_type] || record.record_type} size="small"
                        sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600, fontSize: 10 }} />
                      {record.is_signed && (
                        <Tooltip title="Assinado"><SignedIcon sx={{ color: '#10b981', fontSize: 18 }} /></Tooltip>
                      )}
                      {record.is_confidential && (
                        <Tooltip title="Confidencial"><LockIcon sx={{ color: '#ef4444', fontSize: 18 }} /></Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {record.content?.substring(0, 150)}...
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af', mt: 1, display: 'block' }}>
                    {formatDate(record.created_at)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {records.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af' }}>
                <DescriptionIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography>Nenhum registro encontrado</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* View Record Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {selectedRecord?.title || recordTypeLabels[selectedRecord?.record_type] || 'Registro'}
          <IconButton onClick={() => setViewOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Paciente</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.clinic_patients?.full_name}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Profissional</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Dr(a). {selectedRecord.clinic_professionals?.full_name}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Tipo</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{recordTypeLabels[selectedRecord.record_type]}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Data</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(selectedRecord.created_at)}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {selectedRecord.diagnosis && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#fef3c7', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>Diagnóstico</Typography>
                  <Typography variant="body2">{selectedRecord.diagnosis} {selectedRecord.diagnosis_code ? `(${selectedRecord.diagnosis_code})` : ''}</Typography>
                </Box>
              )}

              <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Conteúdo</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRecord.content}</Typography>
              </Paper>

              {selectedRecord.treatment_plan && (
                <Paper sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#166534' }}>Plano de Tratamento</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRecord.treatment_plan}</Typography>
                </Paper>
              )}

              {selectedRecord.prescriptions?.length > 0 && (
                <Paper sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e40af' }}>Prescrições</Typography>
                  {selectedRecord.prescriptions.map((p: any, i: number) => (
                    <Box key={i} sx={{ mb: 1, pl: 2, borderLeft: '3px solid #3b82f6' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.medication}</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {p.dosage} • {p.frequency} • {p.duration} {p.instructions ? `- ${p.instructions}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                {selectedRecord.is_signed ? (
                  <Chip icon={<SignedIcon />} label={`Assinado em ${formatDate(selectedRecord.signed_at)}`}
                    sx={{ bgcolor: '#d1fae5', color: '#059669', fontWeight: 600 }} />
                ) : (
                  <Button variant="outlined" startIcon={<SignedIcon />}
                    onClick={() => handleSign(selectedRecord.id, selectedRecord.professional_id)}
                    sx={{ color: '#8b5cf6', borderColor: '#8b5cf6', textTransform: 'none' }}>
                    Assinar Digitalmente
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* New Record Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          Novo Registro Clínico
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={patients}
                getOptionLabel={(p: any) => p?.full_name ?? ''}
                value={patients.find((p: any) => p.id === formData.patient_id) ?? null}
                onChange={(_, v) => setFormData({ ...formData, patient_id: (v as any)?.id ?? '' })}
                isOptionEqualToValue={(opt, val) => (opt as any)?.id === (val as any)?.id}
                renderInput={(params) => <TextField {...params} label="Paciente *" size="small" placeholder="Buscar paciente..." />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Profissional *</InputLabel>
                <Select
                  value={formData.professional_id}
                  label="Profissional *"
                  onChange={(e) => setFormData({ ...formData, professional_id: String(e.target.value) })}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Selecione o profissional</em>
                  </MenuItem>
                  {professionals.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Registro</InputLabel>
                <Select value={formData.record_type} label="Tipo de Registro" onChange={(e) => setFormData({ ...formData, record_type: e.target.value as RecordType })}>
                  {Object.entries(recordTypeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Título" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} size="small" />
            </Grid>
          </Grid>

          {/* Anamnese fields */}
          {formData.record_type === 'anamnesis' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Anamnese</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth label="Queixa Principal" value={formData.anamnesis_data.chief_complaint} onChange={(e) => setFormData({ ...formData, anamnesis_data: { ...formData.anamnesis_data, chief_complaint: e.target.value } })} size="small" multiline rows={2} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="História da Doença Atual" value={formData.anamnesis_data.history_present_illness} onChange={(e) => setFormData({ ...formData, anamnesis_data: { ...formData.anamnesis_data, history_present_illness: e.target.value } })} size="small" multiline rows={3} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Antecedentes Pessoais" value={formData.anamnesis_data.past_medical_history} onChange={(e) => setFormData({ ...formData, anamnesis_data: { ...formData.anamnesis_data, past_medical_history: e.target.value } })} size="small" multiline rows={2} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Antecedentes Familiares" value={formData.anamnesis_data.family_history} onChange={(e) => setFormData({ ...formData, anamnesis_data: { ...formData.anamnesis_data, family_history: e.target.value } })} size="small" multiline rows={2} /></Grid>
              </Grid>
            </Box>
          )}

          {/* Exame físico */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Exame Físico (opcional)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}><TextField fullWidth label="PA" value={formData.physical_exam.blood_pressure} onChange={(e) => setFormData({ ...formData, physical_exam: { ...formData.physical_exam, blood_pressure: e.target.value } })} size="small" placeholder="120/80" /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="FC (bpm)" value={formData.physical_exam.heart_rate} onChange={(e) => setFormData({ ...formData, physical_exam: { ...formData.physical_exam, heart_rate: e.target.value } })} size="small" /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Temp (°C)" value={formData.physical_exam.temperature} onChange={(e) => setFormData({ ...formData, physical_exam: { ...formData.physical_exam, temperature: e.target.value } })} size="small" /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Peso (kg)" value={formData.physical_exam.weight} onChange={(e) => setFormData({ ...formData, physical_exam: { ...formData.physical_exam, weight: e.target.value } })} size="small" /></Grid>
            </Grid>
          </Box>

          {/* Prescrição */}
          {formData.record_type === 'prescription' && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Prescrições</Typography>
                <Button size="small" onClick={addPrescription} startIcon={<AddIcon />} sx={{ textTransform: 'none' }}>Adicionar</Button>
              </Box>
              {formData.prescriptions.map((p, i) => (
                <Grid container spacing={1} key={i} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={3}><TextField fullWidth label="Medicamento" value={p.medication} onChange={(e) => { const ps = [...formData.prescriptions]; ps[i].medication = e.target.value; setFormData({ ...formData, prescriptions: ps }); }} size="small" /></Grid>
                  <Grid item xs={6} sm={2}><TextField fullWidth label="Dosagem" value={p.dosage} onChange={(e) => { const ps = [...formData.prescriptions]; ps[i].dosage = e.target.value; setFormData({ ...formData, prescriptions: ps }); }} size="small" /></Grid>
                  <Grid item xs={6} sm={2}><TextField fullWidth label="Frequência" value={p.frequency} onChange={(e) => { const ps = [...formData.prescriptions]; ps[i].frequency = e.target.value; setFormData({ ...formData, prescriptions: ps }); }} size="small" /></Grid>
                  <Grid item xs={6} sm={2}><TextField fullWidth label="Duração" value={p.duration} onChange={(e) => { const ps = [...formData.prescriptions]; ps[i].duration = e.target.value; setFormData({ ...formData, prescriptions: ps }); }} size="small" /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth label="Instruções" value={p.instructions} onChange={(e) => { const ps = [...formData.prescriptions]; ps[i].instructions = e.target.value; setFormData({ ...formData, prescriptions: ps }); }} size="small" /></Grid>
                </Grid>
              ))}
            </Box>
          )}

          {/* Conteúdo principal */}
          <TextField fullWidth label="Conteúdo / Descrição *" value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            multiline rows={6} sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Diagnóstico" value={formData.diagnosis} onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="CID-10" value={formData.diagnosis_code} onChange={(e) => setFormData({ ...formData, diagnosis_code: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Plano de Tratamento" value={formData.treatment_plan} onChange={(e) => setFormData({ ...formData, treatment_plan: e.target.value })} size="small" multiline rows={3} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar Registro'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicalRecords;
